import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { api } from "@/api";
import { RoleBadge } from "@/components/Shared";
import {
  LayoutDashboard, Truck, Users, SlidersHorizontal, Package, ShoppingCart,
  Receipt, BookOpen, Landmark, Wallet, UserCog, Wrench, Settings, LogOut, ExternalLink, FileSpreadsheet,
  FileText, UserCircle, Contact, Menu, ArrowLeftRight, Forklift,
} from "lucide-react";
import Dashboard from "./Dashboard";
import Vendors from "./Vendors";
import Clients from "./Clients";
import Parties from "./Parties";
import Products from "./Products";
import Slabs from "./Slabs";
import Purchases from "./Purchases";
import Invoices from "./Invoices";
import InvoiceNew from "./InvoiceNew";
import Quotations from "./Quotations";
import QuotationNew from "./QuotationNew";
import Ledgers from "./Ledgers";
import Banks from "./Banks";
import Payments from "./Payments";
import Transport from "./Transport";
import Cashflow from "./Cashflow";
import TeamUsers from "./Users";
import CustomFields from "./CustomFields";
import SettingsPage from "./Settings";
import GstReports from "./GstReports";
import Profile from "./Profile";

const NAV = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors" },
  { to: "/app/clients", label: "Clients B2B/B2C", icon: Users, testid: "nav-clients" },
  { to: "/app/parties", label: "Parties Directory", icon: Contact, testid: "nav-parties" },
  { to: "/app/slabs", label: "Price Slabs", icon: SlidersHorizontal, testid: "nav-slabs" },
  { to: "/app/products", label: "Products & Stock", icon: Package, testid: "nav-products" },
  { to: "/app/purchases", label: "Purchases", icon: ShoppingCart, testid: "nav-purchases" },
  { to: "/app/invoices", label: "Tax Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/app/quotations", label: "Proforma / Quotes", icon: FileText, testid: "nav-quotations" },
  { to: "/app/ledgers", label: "Party Ledgers", icon: BookOpen, testid: "nav-ledgers" },
  { to: "/app/payments", label: "Payments", icon: ArrowLeftRight, testid: "nav-payments" },
  { to: "/app/transport", label: "Transport", icon: Forklift, testid: "nav-transport" },
  { to: "/app/banks", label: "Bank & Cash", icon: Landmark, testid: "nav-banks" },
  { to: "/app/cashflow", label: "Income & Expense", icon: Wallet, testid: "nav-cashflow" },
  { to: "/app/reports", label: "GST Reports", icon: FileSpreadsheet, testid: "nav-gst-reports" },
  { to: "/app/employees", label: "Team & Users", icon: UserCog, testid: "nav-employees", admin: true },
  { to: "/app/custom-fields", label: "Custom Fields", icon: Wrench, testid: "nav-custom-fields", admin: true },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings", admin: true },
  { to: "/app/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile" },
];

function CompanyMeta() {
  const [meta, setMeta] = useState({ name: "GreeErgy Green Tech Pvt Ltd", fy: "FY 2026-27" });
  useEffect(() => {
    api.get("/settings").then((r) => {
      setMeta({
        name: r.data?.company_name || "GreeErgy Green Tech Pvt Ltd",
        fy: r.data?.financial_year || "FY 2026-27",
      });
    }).catch(() => {});
  }, []);
  return (
    <>
      <span className="text-slate-800 font-medium" data-testid="portal-company-name">{meta.name}</span>
      <span className="mx-2 text-slate-600">·</span>
      <span data-testid="portal-fy-label">{meta.fy}</span>
    </>
  );
}

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const items = NAV.filter((n) => !n.admin || user?.role === "admin");

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F3F6F5] text-slate-900 flex" data-testid="portal-shell">
      {/* Sidebar */}
      {navOpen && (
        <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setNavOpen(false)} data-testid="nav-backdrop" />
      )}
      <aside className={`w-60 shrink-0 border-r border-slate-200 bg-white/95 backdrop-blur-xl h-screen flex flex-col fixed lg:sticky top-0 z-40 transition-transform duration-300 ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="portal-sidebar">
        <div className="p-4 border-b border-slate-200">
          <Link to="/" className="inline-flex" data-testid="portal-home-link">
            <span className="bg-white rounded-lg px-2.5 py-1 inline-flex">
              <img src="/logo.png" alt="GREENRGY" className="h-6" />
            </span>
          </Link>
          <p className="text-[10px] font-mono2 uppercase tracking-[0.2em] text-slate-500 mt-2">GREENRGY · ACCOUNTS</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-emerald-500/15 text-emerald-800 font-semibold border border-emerald-500/30 shadow-sm"
                    : "text-black font-medium hover:text-emerald-700 hover:bg-emerald-50/60 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-600 font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate" data-testid="portal-user-name">{user?.name}</p>
              <RoleBadge role={user?.role} />
            </div>
          </div>
          <button onClick={doLogout} data-testid="portal-logout-btn"
            className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-500/10 transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-slate-200 bg-white/70 backdrop-blur-xl px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-20" data-testid="portal-top-navbar">
          <div className="flex items-center gap-3">
            <button onClick={() => setNavOpen(true)} data-testid="nav-hamburger-btn"
              className="lg:hidden rounded-lg border border-slate-300 p-2 text-slate-700 hover:bg-slate-100 transition-colors duration-150">
              <Menu className="w-4 h-4" />
            </button>
            <p className="text-sm text-slate-600 truncate">
              <CompanyMeta />
            </p>
          </div>
          <Link to="/" className="text-xs text-slate-500 hover:text-emerald-600 inline-flex items-center gap-1 transition-colors duration-200" data-testid="portal-view-site-link">
            View site <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <div className="p-6 lg:p-8 max-w-[1400px]">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="clients" element={<Clients />} />
            <Route path="parties" element={<Parties />} />
            <Route path="slabs" element={<Slabs />} />
            <Route path="products" element={<Products />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/new" element={<InvoiceNew />} />
            <Route path="invoices/:invoiceId/edit" element={<InvoiceNew />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/new" element={<QuotationNew />} />
            <Route path="quotations/:quotationId/edit" element={<QuotationNew />} />
            <Route path="ledgers" element={<Ledgers />} />
            <Route path="payments" element={<Payments />} />
            <Route path="transport" element={<Transport />} />
            <Route path="banks" element={<Banks />} />
            <Route path="cashflow" element={<Cashflow />} />
            <Route path="reports" element={<GstReports />} />
            <Route path="employees" element={user?.role === "admin" ? <TeamUsers /> : <Navigate to="/app" replace />} />
            <Route path="custom-fields" element={user?.role === "admin" ? <CustomFields /> : <Navigate to="/app" replace />} />
            <Route path="settings" element={user?.role === "admin" ? <SettingsPage /> : <Navigate to="/app" replace />} />
            <Route path="profile" element={<Profile />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
